/*
 * The install offer (docs/PWA.md "Install"): an unobtrusive, dismissible banner from the second visit on, never in
 * the installed app. On Chromium it wraps `beforeinstallprompt`; on iOS Safari (no such event) it explains
 * Share → Add to Home Screen. A dismissal or an install is remembered (localStorage, in try/catch: without storage
 * the banner never shows, since a dismissal could not be remembered).
 */

export const INSTALL_KEY = 'algoboo.install.v1'
export const VISITS_KEY = 'algoboo.visits.v1'
const SESSION_KEY = 'algoboo.visit.counted'
/** From which visit (browser session) on the banner may show. */
export const INSTALL_FROM_VISIT = 2
/** A short delay after load so the banner never competes with the first paint. */
export const INSTALL_DELAY_MS = 4_000

export interface BeforeInstallPromptEvent extends Event {
  prompt(): Promise<void>
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>
}

export type InstallOffer = 'none' | 'prompt' | 'ios'

export function installOffer(s: { standalone: boolean; dismissed: boolean; visits: number; hasPromptEvent: boolean; iosSafari: boolean }): InstallOffer {
  if (s.standalone || s.dismissed || s.visits < INSTALL_FROM_VISIT) return 'none'
  if (s.hasPromptEvent) return 'prompt'
  if (s.iosSafari) return 'ios'
  return 'none'
}

/** iPhone / iPad Safari (iPadOS reports a Mac user agent with touch). */
export function isIosSafari(ua: string, maxTouchPoints = 0): boolean {
  const ios = /iP(hone|od|ad)/.test(ua) || (/Macintosh/.test(ua) && maxTouchPoints > 1)
  return ios && /WebKit/.test(ua) && !/(CriOS|FxiOS|EdgiOS|OPiOS)/.test(ua)
}

/** Any iOS / iPadOS browser (all of them are WebKit; web push needs the installed app there). */
export function isIos(ua: string, maxTouchPoints = 0): boolean {
  return /iP(hone|od|ad)/.test(ua) || (/Macintosh/.test(ua) && maxTouchPoints > 1)
}

export function installDismissed(): boolean {
  try {
    return localStorage.getItem(INSTALL_KEY) !== null
  } catch {
    return true // no storage: never nag, we could not remember a dismissal
  }
}

export function markInstallShown(outcome: string): void {
  try {
    localStorage.setItem(INSTALL_KEY, outcome)
  } catch {
    // storage blocked
  }
}

/** Count this browser session as a visit (once per session) and return the visit count. */
export function countVisit(): number {
  try {
    const n = Number(localStorage.getItem(VISITS_KEY) ?? '0') || 0
    if (sessionStorage.getItem(SESSION_KEY)) return n
    sessionStorage.setItem(SESSION_KEY, '1')
    localStorage.setItem(VISITS_KEY, String(n + 1))
    return n + 1
  } catch {
    return 0
  }
}

// `beforeinstallprompt` can fire before React mounts: capture it from the entry (main.tsx) and hand it to the banner.
let captured: BeforeInstallPromptEvent | null = null
const promptListeners = new Set<(e: BeforeInstallPromptEvent | null) => void>()
let capturing = false

/** Start listening for `beforeinstallprompt` / `appinstalled` (idempotent; call once at startup). */
export function captureInstallPrompt(target: Pick<Window, 'addEventListener'> = window): void {
  if (capturing) return
  capturing = true
  target.addEventListener('beforeinstallprompt', (e) => {
    e.preventDefault() // no mini-infobar: the banner offers it at the right time
    captured = e as BeforeInstallPromptEvent
    for (const l of promptListeners) l(captured)
  })
  target.addEventListener('appinstalled', () => {
    captured = null
    for (const l of promptListeners) l(null)
  })
}

/** The captured prompt event, now and whenever it changes. */
export function onInstallPrompt(fn: (e: BeforeInstallPromptEvent | null) => void): () => void {
  captureInstallPrompt()
  promptListeners.add(fn)
  if (captured) fn(captured)
  return () => {
    promptListeners.delete(fn)
  }
}
